import { Box, MenuItem, Popover, Stack, useTheme } from "@mui/material";
import {
  DropdownMenu,
  PlusDualIcon,
  SingleUserIcon,
  TicketIcon,
  UserCircleIcon,
  UserIcon,
} from "components";
import { usePermissionStore } from "controller";
import { CustomerOnBoardingDrawer } from "pages/modules/customer/customers";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "shared";
import { CreateTicketDrawer } from "shared/features/new-ticket/CreateTicketDrawer";
import { addMixpanelEvent } from "utilz/helpers";

export const NewButton = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [drawerType, setDrawerType] = useState<string | null>(null);

  const [optionsAnchorEl, setOptionsAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const handleClickAway = (e: any, option: any) => {
    e.stopPropagation();
    setOptionsAnchorEl(null);
  };

  const mixpanelEventLogging = (event: string) => {
    let eventKey = "shortcut_new_ticket_opened";
    switch (event) {
      case "ticket":
        eventKey = "shortcut_new_ticket_opened";
        break;
      case "customer":
        eventKey = "shortcut_new_customer_opened";
        break;
      case "user":
        eventKey = "shortcut_new_user_opened";
        break;
      default:
        break;
    }
    addMixpanelEvent({ event: eventKey as any });
  };
  const handleOptionClick = (e: any, option: any) => {
    e.stopPropagation();
    setOptionsAnchorEl(null);
    mixpanelEventLogging(option.value);
    setDrawerType(option.value);

    // onOptionClick && onOptionClick(option, e);
  };

  const haveFullCustomerAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.CUSTOMER.CUSTOMER_ALL_RW]),
  );
  const haveTicketAccess = usePermissionStore((state) =>
    state.hasPermissions(
      [PERMISSIONS.SD.SD_TICKET_ALL_RW, PERMISSIONS.SD.SD_TICKET_ASSIGNED_RW],
      "OR",
    ),
  );
  const haveUserAccess = usePermissionStore((state) =>
    state.hasPermissions(
      [
        PERMISSIONS.CUSTOMER.CUSTOMER_ALL_RW,
        PERMISSIONS.CUSTOMER.CUSTOMER_CUSTOM_RW,
      ],
      "OR",
    ),
  );
  const options = [
    ...(!haveTicketAccess
      ? []
      : [
          {
            name: "New ticket",
            value: "ticket",
            type: "ticket",
            icon: <TicketIcon />,
          },
        ]),
    ...(!haveFullCustomerAccess
      ? []
      : [
          {
            name: "New customer",
            value: "customer",
            type: "customer",
            icon: <UserIcon />,
          },
        ]),
    ...(!haveUserAccess
      ? []
      : [
          {
            name: "New user",
            value: "user",
            type: "user",
            icon: <SingleUserIcon />,
          },
        ]),
  ];
  return (
    <>
      {options.length > 0 && (
        <>
          <Box
            onMouseEnter={(e) => {
              setOptionsAnchorEl(e.currentTarget);
            }}
            onMouseLeave={() => {
              setOptionsAnchorEl(null);
            }}
            sx={{ position: "relative" }}
          >
            <Stack
              sx={{
                height: "32px",
                width: "32px",
                borderRadius: "6px",
                border: `1px solid ${theme.palette.neutral["020"]}`,
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                ":hover": {
                  backgroundColor: theme.palette.neutral["010"],
                },
              }}
            >
              <PlusDualIcon
                fontSize="medium"
                htmlColor={theme.palette.primary.main}
              />
            </Stack>

            <Popover
              open={Boolean(optionsAnchorEl)}
              anchorEl={optionsAnchorEl}
              onClose={handleClickAway}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              disableRestoreFocus
              sx={{
                pointerEvents: "none",
                "& .MuiPopover-paper": {
                  pointerEvents: "auto",
                },
              }}
              PaperProps={{
                onMouseEnter: () => {
                  // Keep popover open when hovering over it
                },
                onMouseLeave: () => {
                  setOptionsAnchorEl(null);
                },
                sx: {
                  minWidth: "175px",
                  maxWidth: "300px", // Added maxWidth to prevent overly wide menus
                  bgcolor: "#FFF",
                  borderRadius: "8px",
                  boxShadow:
                    "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
                  border: `1px solid ${theme.palette.neutral["015"]}`,
                  // mt: -1, // Negative margin to reduce gap between trigger and popover
                },
              }}
            >
              {options?.map((option) => (
                <MenuItem
                  key={`${option?.value}-${option?.type}-${option.name}`}
                  sx={{
                    display: "flex",
                    flexGrow: 1,
                    direction: "row",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: "175px",
                    width: "100%",
                    maxWidth: "100%",
                    whiteSpace: "normal", // Allow text to wrap
                    minHeight: "40px", // Ensure consistent height
                    px: 2, // Add horizontal padding
                    py: 1, // Add vertical padding
                    // backgroundColor:
                    //   option?.value === selectedValue
                    //     ? theme.palette.neutral["005"]
                    //     : "",
                    ":hover": {
                      bgcolor: theme.palette.neutral["B-005"],
                    },
                  }}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleOptionClick(e, option);
                  }}
                >
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexGrow: 1,
                        alignItems: "center",
                        minWidth: 0, // Important for text overflow
                      }}
                    >
                      {option?.icon && (
                        <Box sx={{ display: "flex", mr: 1 }}>{option.icon}</Box>
                      )}
                      <Box
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          ...theme.typography["body-small"],
                        }}
                      >
                        {option.name}
                      </Box>
                    </Box>
                  </>
                </MenuItem>
              ))}
            </Popover>
          </Box>

          {drawerType === "ticket" && (
            <CreateTicketDrawer
              open={true}
              onClose={(e, type) => {
                setDrawerType(null);
                if (type === "save") {
                  navigate("/app/service-desk/tickets");
                }
              }}
              type="ticket"
            />
          )}
          {drawerType === "customer" || drawerType === "user" ? (
            <CustomerOnBoardingDrawer
              open={drawerType === "customer" || drawerType === "user"}
              // onClose={() => setIsOnboardingDrawerOpen(false)}
              onClose={(e, type) => {
                const currentDrawer = drawerType;
                setDrawerType(null);
                if (type !== "save") return;
                if (currentDrawer === "customer") {
                  navigate("/app/customers/customers");
                } else if (currentDrawer === "user") {
                  navigate("/app/customers/users/home");
                }
              }}
              onBoarding={drawerType}
            />
          ) : null}
        </>
      )}
    </>
  );
};
